import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import usePageTitle from "@/hooks/use-page-title";

export default function ChangePassword() {
  usePageTitle("Change Password");
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const forced = !!user?.must_change_password;
  const [oldPwd, setOldPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (newPwd !== confirm) return setErr("Passwords do not match");
    if (newPwd.length < 6) return setErr("Password must be at least 6 characters");
    try {
      const payload = { new_password: newPwd };
      if (!forced) payload.old_password = oldPwd;
      await api.post("/auth/change-password", payload);
      setOk(true);
      if (user) {
        const updated = { ...user, must_change_password: false };
        setUser && setUser(updated);
      }
      setTimeout(() => nav(user?.role === "customer" ? "/portal" : "/dashboard"), 800);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--background))] p-6">
      <div className="w-full max-w-md space-y-6">
        <div className="bg-black inline-flex px-4 py-3">
          <Logo height={44} variant="dark" />
        </div>
        <div>
          <div className="overline mb-2">{forced ? "First-time setup" : "Security"}</div>
          <h1 className="font-display text-4xl font-black tracking-tighter">
            {forced ? "Set your password" : "Change password"}
          </h1>
          {forced && (
            <p className="mt-3 text-sm text-muted-foreground">
              For your security, please choose a new password before continuing.
            </p>
          )}
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
          {!forced && (
            <div>
              <label className="overline block mb-2">Current password</label>
              <input
                type="password"
                required
                value={oldPwd}
                onChange={(e) => setOldPwd(e.target.value)}
                className="w-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2.5 outline-none focus:border-[#FF6B00]"
                data-testid="old-password-input"
              />
            </div>
          )}
          <div>
            <label className="overline block mb-2">New password</label>
            <input
              type="password"
              required
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              className="w-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2.5 outline-none focus:border-[#FF6B00]"
              data-testid="new-password-input"
            />
          </div>
          <div>
            <label className="overline block mb-2">Confirm new password</label>
            <input
              type="password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2.5 outline-none focus:border-[#FF6B00]"
              data-testid="confirm-password-input"
            />
          </div>
          {err && <div className="text-sm text-red-600" data-testid="change-error">{err}</div>}
          {ok && <div className="text-sm text-green-600" data-testid="change-success">Password updated. Redirecting…</div>}
          <button type="submit" className="btn-swiss btn-accent w-full justify-center py-3" data-testid="change-submit">
            Save Password
          </button>
        </form>
      </div>
    </div>
  );
}
